<template>
  <div class="camera-panel bg-white h-full flex flex-col">
    <!-- Header -->
    <div class="p-4 border-b flex items-center justify-between">
      <h2 class="text-lg font-semibold text-gray-800">
        <i class="fas fa-video text-blue-500 mr-2"></i>
        CCTV Cameras
        <span class="ml-2 text-sm font-normal text-gray-500">({{ filteredCameras.length }})</span>
      </h2>
      <div class="flex items-center space-x-2">
        <button 
          @click="refreshCameras"
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          :disabled="loading"
          title="Refresh"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
        </button>
        <button 
          @click="toggleFullscreen"
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
        >
          <i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
        </button>
        <button 
          @click="$emit('close')"
          class="p-2 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
          title="Close"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>
    
    <!-- Search -->
    <div class="p-4 border-b">
      <div class="relative">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search cameras..."
          class="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <i class="fas fa-search absolute left-3 top-3 text-gray-400"></i>
      </div>
    </div>
    
    <!-- Camera Grid with IFRAME -->
    <div class="flex-1 overflow-y-auto p-4">
      <div v-if="loading" class="flex flex-col items-center justify-center h-64">
        <div class="spinner mb-3"></div>
        <p class="text-sm text-gray-500">Loading cameras...</p>
      </div>
      
      <div v-else-if="filteredCameras.length === 0" class="flex flex-col items-center justify-center h-64 text-gray-500">
        <i class="fas fa-video-slash text-5xl mb-3 opacity-30"></i>
        <p class="text-sm">No cameras found</p>
      </div>
      
      <div v-else class="camera-grid">
        <div 
          v-for="camera in filteredCameras" 
          :key="camera.name || camera.id"
          class="camera-card group"
          @click="selectCamera(camera)"
        >
          <div class="camera-player">
            <!-- IFRAME instead of video -->
            <iframe
              :src="camera.url"
              class="w-full h-full"
              allow="autoplay; encrypted-media; fullscreen; camera; microphone"
              frameborder="0"
              allowfullscreen
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals"
              referrerpolicy="no-referrer-when-downgrade"
            ></iframe>
            
            <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
              <div class="flex items-center justify-between">
                <div class="flex-1 min-w-0">
                  <h3 class="text-white font-medium text-sm truncate" :title="camera.name">
                    {{ camera.name }}
                  </h3>
                  <p class="text-white/70 text-xs mt-0.5">
                    {{ camera.source || 'Unknown source' }}
                  </p>
                </div>
                <span 
                  class="w-2 h-2 rounded-full flex-shrink-0 ml-2 bg-green-500"
                  title="Live"
                ></span>
              </div>
            </div>
            
            <button
              @click.stop="fullscreenCamera(camera)"
              class="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-lg flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
              title="Fullscreen"
            >
              <i class="fas fa-expand"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Footer Stats -->
    <div class="border-t p-3 bg-gray-50 text-xs text-gray-600">
      <div class="flex justify-between">
        <span>🟢 Online: {{ filteredCameras.length }}</span>
        <span>📹 Total: {{ filteredCameras.length }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { useCamerasStore } from '../../stores/cameras'

const props = defineProps({
  initialCameras: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'select-camera'])

const authStore = useAuthStore()
const camerasStore = useCamerasStore()

// State
const cameras = ref([])
const loading = ref(false)
const searchQuery = ref('')
const isFullscreen = ref(false)

// Computed
const filteredCameras = computed(() => {
  let result = cameras.value
  
  if (authStore.userSource !== 'all') {
    result = result.filter(cam => cam.source === authStore.userSource)
  }
  
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(cam => 
      cam.name?.toLowerCase().includes(query) ||
      cam.source?.toLowerCase().includes(query)
    )
  }
  
  return result
})

async function fetchCameras() {
  loading.value = true
  
  try {
    await camerasStore.fetchCameras()
    cameras.value = camerasStore.cameras || props.initialCameras || []
  } catch (error) {
    console.error('Failed to fetch cameras:', error)
  } finally {
    loading.value = false
  }
}

function selectCamera(camera) {
  emit('select-camera', camera)
}

function refreshCameras() {
  fetchCameras()
}

function toggleFullscreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen()
    isFullscreen.value = true
  } else {
    document.exitFullscreen()
    isFullscreen.value = false
  }
}

function fullscreenCamera(camera) {
  // For iframe fullscreen, we need to find the iframe element
  const iframe = document.querySelector(`.camera-card iframe[src="${camera.url}"]`)
  if (iframe) {
    if (iframe.requestFullscreen) {
      iframe.requestFullscreen()
    } else if (iframe.webkitRequestFullscreen) {
      iframe.webkitRequestFullscreen()
    }
  }
}

onMounted(() => {
  fetchCameras()
})
</script>

<style scoped>
.camera-panel {
  width: 480px;
}

@media (max-width: 1024px) {
  .camera-panel {
    width: 100%;
    max-width: 480px;
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 30;
  }
}

.camera-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.camera-card {
  position: relative;
  background: #f3f4f6;
  border-radius: 0.75rem;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.camera-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}

.camera-player {
  position: relative;
  padding-bottom: 56.25%;
  background: #000;
}

.camera-player iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0,0,0,0.1);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>